/**
 * One agent session in a component: hydrate (replay its events), stream live, send messages with the
 * current ScreenContext, answer questions, cancel, act on changesets and composer edits.
 *
 * The surface is the same everywhere (assistant panel, Home, queue, inbox thread, CRM review); hosts only
 * pass the session id (or nothing, to start one on first send) plus entity refs / surface for new sessions.
 */
import { computed, onBeforeUnmount, reactive, ref, watch, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { agentApi, AgentApiError, type Changeset, type ChatMention, type SessionDetail, type UploadedFile, type VoiceTalker } from './api'
import {
    appendCachedEvent,
    fetchEventsAfter,
    fetchSession,
    getCachedSession,
    inflightSession,
    updateCachedDetail,
    type CachedSession,
} from './sessionCache'
import { recordsWarming } from './recordCache'
import { dispatchAction, onComposerEditResolved } from './actions'
import type { AnyAgentEvent, AttachmentPayload } from './protocol/types'
import {
    addLocalUserMessage,
    expireClientCalls,
    initialState,
    isWorking,
    pendingApprovals,
    reduce,
    seedAttachments,
    seedBrowser,
    seedTranscript,
    type SessionState,
} from './reducer'
import { setSessionWorking } from './workingSessions'
import { snapshotScreen } from './screen/useScreenContext'
import { streamEvents, type StreamStatus } from './stream'
import { refreshUsage } from './usage'
import { withDesktopClient } from '@/desktop/clientTools'
import { notifyClientCall, notifyLiveRunFinished } from '@/desktop/notify'
import { emitGuideSignal } from '@/guide/runtime/signals'

/** How long a first open waits for its cards' records before painting without them (they fade in when they land). */
const RECORDS_WAIT_MS = 600

export interface SessionOptions {
    /** For a session created on first send. */
    entityRefs?: { type: string; id: string }[]
    surface?: string
    title?: string
    tier?: 'quick' | 'standard' | 'deep'
    /** Don't attach the ScreenContext (e.g. a full-page chat with nothing else on screen). */
    noScreen?: boolean
    /** Don't open the live stream (tests, read-only previews). */
    noStream?: boolean
    onSessionCreated?: (id: string) => void
    /** A live `open` action: return true if the host showed it (e.g. in its workspace pane) instead of routing. */
    onOpen?: (target: Record<string, any>) => boolean
    /** The space the live browser would get beside the chat: a browser the turn opens by itself takes its shape. */
    browserPane?: () => { width: number; height: number } | null
    /**
     * Whether this instance runs live `ui.action`s (default: always). A second instance of a conversation that
     * may also be on screen (the voice call's) passes `() => !isSessionMounted(id)`, so actions run once.
     * Instances with this option don't count as "mounted" themselves.
     */
    dispatchActions?: () => boolean
}

export interface SendOptions {
    files?: File[]
    tier?: 'quick' | 'standard' | 'deep'
    /** Spoken on a voice call (the backend keeps the reply short and speakable; older backends ignore it). */
    inputMode?: 'text' | 'voice'
    /** Teammates @mentioned with the composer's picker (user ids). */
    mentions?: string[]
    /** Said while the run works (a voice call): the run takes it mid-task instead of the send failing busy. */
    steer?: boolean
    /** The call's voice between replies (voice turns): `server` talker or `realtime`. */
    voiceTalker?: VoiceTalker
}

/** Who a chat belongs to and who it's shared with (workspace members; `GET /sessions/:id`). */
export interface ChatAccess {
    createdBy: string | null
    createdByName: string | null
    sharedAt: string | null
    canShare: boolean
    /** The viewer's own open @mention here (a teammate pinged them), if any. */
    myMention: ChatMention | null
    mentions: ChatMention[]
}

function emptyAccess(): ChatAccess {
    return { createdBy: null, createdByName: null, sharedAt: null, canShare: false, myMention: null, mentions: [] }
}

// Conversations shown by a primary session instance (an AgentSurface), reference-counted. Reactive, so a
// view can follow whether a conversation is on screen (the call pill hides behind the chat's header chip).
const mounted = reactive(new Map<string, number>())
function mountSession(id: string | null, delta: 1 | -1): void {
    if (!id) return
    const n = (mounted.get(id) ?? 0) + delta
    if (n > 0) mounted.set(id, n)
    else mounted.delete(id)
}
/** Is this conversation on screen in a surface that runs its actions? */
export function isSessionMounted(id: string | null | undefined): boolean {
    return Boolean(id && mounted.get(id))
}

export function useAgentSession(sessionId: Ref<string | null>, opts: SessionOptions = {}) {
    const state = reactive(initialState(sessionId.value)) as SessionState
    const status = ref<StreamStatus>('closed')
    const loading = ref(false)
    const sending = ref(false)
    /**
     * Between the send POST returning and the stream's `run.started` (the run can queue for several seconds) nothing
     * else says "the agent has your message", so the typing dots would vanish. Held until a new run starts.
     */
    const awaitingFrom = ref<number | null>(null)
    let awaitingTimer: ReturnType<typeof setTimeout> | null = null
    const AWAITING_MAX_MS = 45_000
    const runsStarted = () => state.handledTypes['run.started'] ?? 0
    const awaiting = computed(() => awaitingFrom.value !== null && runsStarted() === awaitingFrom.value)
    function stopAwaiting() {
        awaitingFrom.value = null
        if (awaitingTimer) clearTimeout(awaitingTimer)
        awaitingTimer = null
    }
    const error = ref<string | null>(null)
    /** The last failed send's HTTP status / error code (the voice call tells "busy" from "out of usage"). */
    const errorInfo = ref<{ status: number; code?: string } | null>(null)
    /** The creator's thumbs on agent replies (message id → rating). */
    const feedback = reactive<Record<string, 'up' | 'down'>>({})
    const access = reactive<ChatAccess>(emptyAccess())
    let router: ReturnType<typeof useRouter> | null = null
    try {
        router = useRouter()
    } catch {
        router = null
    }
    let ctrl: AbortController | null = null
    let liveFrom = Number.MAX_SAFE_INTEGER // only actions from live events (not replay) are dispatched
    const handled = new Set<number>()

    function apply(ev: AnyAgentEvent): void {
        appendCachedEvent(state.sessionId, ev)
        reduce(state, ev)
        if (ev.type === 'run.finished' && ev.id >= liveFrom) {
            // A finished run has just added to the month's usage: update the composer's meter.
            void refreshUsage({ force: true })
            // A guide step watching a reply (onboarding's Home mission) moves on.
            emitGuideSignal('agent.reply_done', { sessionId: sessionId.value })
            // Desktop app: the creator switched away while it worked (no-op elsewhere).
            notifyLiveRunFinished(ev, { sessionId: state.sessionId, title: state.title, lastAssistantText: lastAssistantText(state) })
        }
        if (ev.type === 'ui.action' && ev.id >= liveFrom) runPendingActions()
        // Desktop app: the agent is waiting on the creator's OK to use their computer (no-op elsewhere).
        if (ev.type === 'client.call' && ev.id >= liveFrom) notifyClientCall(ev, state.sessionId)
    }

    function runPendingActions(): void {
        if (opts.dispatchActions && !opts.dispatchActions()) {
            // Someone else runs them (the surface on screen): just don't run these later.
            for (const a of state.actions) handled.add(a.eventId)
            return
        }
        for (const a of state.actions) {
            if (handled.has(a.eventId) || a.eventId < liveFrom) continue
            handled.add(a.eventId)
            if (a.action === 'open' && opts.onOpen?.(a.target)) continue
            dispatchAction(a, { push: (p) => router?.push(p) })
        }
    }

    function reset(id: string | null): void {
        Object.assign(state, initialState(id))
        Object.assign(access, emptyAccess())
        handled.clear()
    }

    /** Detail fields that may change without an event (title, links, thumbs). */
    function applyDetail(detail: SessionDetail): void {
        state.title = detail.title
        state.initiatedBy = detail.initiated_by ?? null
        state.linkedThread = detail.linked_thread ?? null
        state.linkedThreads = detail.linked_threads ?? []
        state.linkedBrand = detail.linked_brand ?? null
        for (const k of Object.keys(feedback)) delete feedback[k]
        Object.assign(feedback, detail.feedback ?? {})
        Object.assign(access, {
            createdBy: detail.created_by ?? null, createdByName: detail.created_by_name ?? null,
            sharedAt: detail.shared_at ?? null, canShare: Boolean(detail.can_share),
            myMention: detail.my_mention ?? null, mentions: detail.mentions ?? [],
        })
    }

    /** Share this chat with the workspace, or stop sharing it. Resolves false (see `error`) when refused. */
    async function share(shared: boolean): Promise<boolean> {
        const id = state.sessionId
        if (!id) return false
        const before = access.sharedAt
        access.sharedAt = shared ? (before ?? new Date().toISOString()) : null
        try {
            access.sharedAt = (await agentApi().shareSession(id, shared)).shared_at ?? null
            return true
        } catch (e) {
            access.sharedAt = before
            error.value = e instanceof AgentApiError ? e.message : 'Could not change who sees this chat.'
            return false
        }
    }

    /** What the detail knows better than old events: files on early messages, current browser task statuses. */
    function settleFromDetail(detail: SessionDetail): void {
        seedAttachments(state, detail.messages)
        // Browser task cards in old events may be stale: the detail has their current status.
        seedBrowser(state, detail.browser_tasks, detail.browser)
        // Desktop asks the log still has as pending but whose time ran out: nobody is waiting on them.
        expireClientCalls(state)
        liveFrom = state.cursor + 1
    }

    /** Build the conversation from its detail and whole log (fresh from the server or from the cache). */
    function paint(entry: CachedSession): void {
        const { detail } = entry
        // A copy: the reducer may keep (and later change) objects from the events, and the cache must not change.
        const events = structuredClone(entry.events)
        applyDetail(detail)
        // Older events were pruned (or never existed: pre-v2 chats): show the stored transcript for that part.
        // A lone session.created is no proof the log is complete (a rename emits one), so look for the
        // first event that carries a message; the stored messages before it are the ones the log lacks.
        const firstMessage = events.find((e) => e.type === 'message.completed')
        const logStart = events[0]
        const logComplete = logStart?.type === 'session.created' && firstMessage
            && detail.messages.every((m) => m.created_at >= logStart.ts)
        if (!logComplete) seedTranscript(state, detail.messages, firstMessage?.ts)
        for (const e of events) reduce(state, e)
        settleFromDetail(detail)
    }

    /**
     * The cards' records (a change's drafts, a draft it opened) follow a beat behind the conversation: wait a moment
     * for a warm-up under way, so it paints whole instead of the cards popping in under the reply. Never for long.
     */
    async function recordsSettled(id: string): Promise<void> {
        const records = recordsWarming(id)
        if (records) await Promise.race([records, new Promise((r) => setTimeout(r, RECORDS_WAIT_MS))])
    }

    async function hydrate(id: string, token: number): Promise<void> {
        loading.value = true
        try {
            // A prefetch already under way (Home warms its first conversation) is waited on, not repeated.
            const entry = await (inflightSession(id) ?? fetchSession(id))
            if (entry && token === loadSeq) await recordsSettled(id)
            if (entry && token === loadSeq) paint(entry)
        } catch (e) {
            if (token === loadSeq) error.value = e instanceof AgentApiError ? e.message : 'Could not load this conversation.'
        } finally {
            if (token === loadSeq) loading.value = false
        }
    }

    /**
     * Painted from the cache: fetch only what changed since (the detail, and events after the last one shown).
     * The new events are replayed, not live, so actions taken while this was closed don't run again on open.
     */
    async function refresh(id: string, token: number): Promise<void> {
        try {
            const [detail, events] = await Promise.all([agentApi().getSession(id), fetchEventsAfter(id, state.cursor)])
            if (token !== loadSeq) return
            updateCachedDetail(id, detail)
            for (const e of events) {
                appendCachedEvent(id, e)
                reduce(state, e)
            }
            applyDetail(detail)
            settleFromDetail(detail)
        } catch {
            // Keep the painted copy; the live stream picks up from where it ends.
        }
    }

    function openStream(id: string): void {
        ctrl?.abort()
        if (opts.noStream) return
        ctrl = new AbortController()
        const c = ctrl
        streamEvents({ sessionId: id, after: state.cursor, signal: c.signal, onEvent: apply,
                       onStatus: (s) => { if (!c.signal.aborted) status.value = s } })
    }

    // Bumped by every load, so a fetch for a conversation that's no longer shown doesn't write into this one.
    let loadSeq = 0
    async function load(id: string | null): Promise<void> {
        ctrl?.abort()
        reset(id)
        error.value = null
        loading.value = false
        const token = ++loadSeq
        if (!id) return
        // Seen before (or prefetched): paint it now, in the same tick as the reset, then check for changes.
        const cached = getCachedSession(id)
        if (cached) {
            // Opened just as its prefetch landed: its records may still be on the way.
            if (recordsWarming(id)) {
                loading.value = true
                await recordsSettled(id)
                if (token !== loadSeq) return
                loading.value = false
            }
            paint(cached)
            await refresh(id, token)
        } else {
            await hydrate(id, token)
        }
        if (token === loadSeq && sessionId.value === id) openStream(id)
    }

    // The session this instance has loaded (or created). A newly created session is already live, so
    // seeing its id come back through the prop must not reload it.
    let loadedFor: string | null | undefined
    const primary = !opts.dispatchActions
    function setLoaded(id: string | null): void {
        if (primary) {
            mountSession(loadedFor ?? null, -1)
            mountSession(id, 1)
        }
        loadedFor = id
    }
    watch(sessionId, (id) => {
        if (id === loadedFor) return
        setLoaded(id)
        load(id)
    }, { immediate: true })

    // Sidebar dot: mirror this chat's working state, keyed by the conversation it's showing.
    watch(() => [state.sessionId, isWorking(state)] as const, ([id, on], prev) => {
        // `prev` is undefined on the immediate first run.
        const prevId = prev?.[0]
        if (prevId && prevId !== id) setSessionWorking(prevId, false)
        setSessionWorking(id, on)
    }, { immediate: true })

    const offComposer = onComposerEditResolved((key, accepted) => resolveComposerEdit(key, accepted))
    onBeforeUnmountSafe(() => {
        ctrl?.abort()
        offComposer()
        setSessionWorking(state.sessionId, false)
        if (primary) mountSession(loadedFor ?? null, -1)
    })

    async function ensureSession(): Promise<string> {
        if (sessionId.value) return sessionId.value
        const s = await agentApi().createSession({ title: opts.title, entity_refs: opts.entityRefs, surface: opts.surface })
        state.sessionId = s.id
        // A new chat is the viewer's own: private to them until they share it.
        Object.assign(access, { ...emptyAccess(), createdBy: s.created_by ?? null, canShare: true })
        liveFrom = 1
        setLoaded(s.id)
        sessionId.value = s.id
        opts.onSessionCreated?.(s.id)
        openStream(s.id)
        return s.id
    }

    /** Resolves the run the message started (or steered, or that the call's voice answered about), or `false` (see `error` / `errorInfo`). */
    async function send(text: string, extra: SendOptions = {}): Promise<{ runId: string; steered?: boolean; answered?: string } | false> {
        // Files alone are a message: the agent gets a plain line to answer instead of an empty turn.
        const body = text.trim() || (extra.files?.length ? FILE_ONLY_MESSAGE : '')
        if (!body || sending.value) return false
        sending.value = true
        awaitingFrom.value = runsStarted()
        if (awaitingTimer) clearTimeout(awaitingTimer)
        awaitingTimer = setTimeout(stopAwaiting, AWAITING_MAX_MS)
        error.value = null
        errorInfo.value = null
        const localId = addLocalUserMessage(state, body, extra.files, extra.inputMode)
        // Until the server names it: the creator's own words (it renames shortly after).
        if (!state.title || state.title === 'New conversation') state.title = quickTitle(body)
        const mode = {
            ...(extra.inputMode ? { input_mode: extra.inputMode } : {}),
            ...(extra.mentions?.length ? { mentions: extra.mentions } : {}),
        }
        try {
            const id = await ensureSession()
            const api = agentApi()
            const attachments = extra.files?.length ? await uploadFiles(id, localId, extra.files) : []
            const q = state.question
            // From the desktop app the turn carries this window's client info, so the agent can ask to use the computer.
            const run = q && !q.answered
                ? await api.answer(id, withDesktopClient({ text: body, question_id: q.id, attachments, ...mode,
                                                           ...(extra.voiceTalker ? { voice_talker: extra.voiceTalker } : {}) }))
                : await api.sendMessage(id, withDesktopClient({
                    text: body, tier: extra.tier ?? opts.tier ?? 'standard', attachments,
                    screen_context: opts.noScreen ? null : snapshotScreen(),
                    browser_pane: opts.browserPane?.() ?? null, ...mode,
                    ...(extra.steer ? { steer: true } : {}), ...(extra.voiceTalker ? { voice_talker: extra.voiceTalker } : {}),
                }))
            const how = run as { steered?: boolean; answered?: string } | undefined
            return { runId: run?.id ?? '', ...(how?.steered ? { steered: true } : {}), ...(how?.answered ? { answered: how.answered } : {}) }
        } catch (e) {
            stopAwaiting()
            delete state.messages[localId]
            state.timeline = state.timeline.filter((t) => !(t.kind === 'message' && t.id === localId))
            error.value = e instanceof AgentApiError ? e.message : 'Could not send your message.'
            errorInfo.value = e instanceof AgentApiError ? { status: e.status, code: e.code } : { status: 0 }
            if (e instanceof AgentApiError && e.code === 'usage_exhausted') void refreshUsage({ force: true })
            return false
        } finally {
            sending.value = false
        }
    }

    /** Upload the message's files; the ones that fail come off the bubble and are named in `error`. */
    async function uploadFiles(id: string, localId: string, files: File[]): Promise<AttachmentPayload[]> {
        const up = await agentApi().uploadAttachments(id, files)
        const shown = state.messages[localId]?.attachments ?? []
        const sent: AttachmentPayload[] = []
        const failed: UploadedFile[] = []
        for (const [i, f] of (up.files ?? []).entries()) {
            if (!f.file_id) {
                failed.push(f)
                continue
            }
            sent.push({ file_id: f.file_id, filename: f.filename ?? files[i]?.name, content_type: f.content_type ?? null, size: f.size ?? null })
            if (shown[i]) shown[i].fileId = f.file_id
        }
        const msg = state.messages[localId]
        if (msg?.attachments) msg.attachments = msg.attachments.filter((a) => a.fileId)
        if (failed.length) {
            const names = failed.map((f) => f.filename).filter(Boolean).join(', ')
            const why = failed[0].error?.replace(/^\w+Error: /, '')
            error.value = `Couldn't attach ${names || 'a file'}${why ? ` (${why})` : ''}. Your message was sent without it.`
        }
        return sent
    }

    async function cancel(): Promise<void> {
        if (state.activeRunId) await agentApi().cancelRun(state.activeRunId)
    }

    async function actOnChangeset(id: string, action: 'approve' | 'reject' | 'undo'): Promise<Changeset | null> {
        error.value = null
        try {
            const cs = await agentApi().changesetAction(id, action)
            const card = state.changesets[id]
            if (card) {
                card.state = action === 'reject' ? 'rejected' : action === 'undo' ? 'undone' : 'applied'
                if (action === 'undo') card.notUndone = cs.not_undone ?? []
            }
            return cs
        } catch (e) {
            error.value = e instanceof AgentApiError ? e.message : `Could not ${action} those changes.`
            return null
        }
    }

    /** Put back some records of an applied change (the changes pane's per-record Undo). Null when it failed. */
    async function undoRecords(id: string, seqs: number[]): Promise<Changeset | null> {
        error.value = null
        try {
            const cs = await agentApi().undoRecords(id, seqs)
            const card = state.changesets[id]
            if (card) {
                card.undone = cs.undone ?? []
                if (cs.status === 'undone') {
                    card.state = 'undone'
                    card.notUndone = cs.not_undone ?? []
                }
            }
            return cs
        } catch (e) {
            error.value = e instanceof AgentApiError ? e.message : 'Could not undo that.'
            return null
        }
    }

    /** Show a proposed composer edit again (e.g. the creator had navigated away from the thread). */
    function reopenComposerEdit(key: string): void {
        const e = state.composerEdits[key]
        if (e && e.state === 'proposed') {
            dispatchAction({ eventId: Number.MAX_SAFE_INTEGER, action: 'insert_into_composer', target: e.target, runId: null },
                           { push: (p) => router?.push(p) })
        }
    }

    function resolveComposerEdit(key: string, accepted: boolean): void {
        const e = state.composerEdits[key]
        if (e) e.state = accepted ? 'accepted' : 'rejected'
    }

    async function rate(messageId: string, rating: 'up' | 'down' | null): Promise<void> {
        const prev = feedback[messageId] ?? null
        if (rating) feedback[messageId] = rating
        else delete feedback[messageId]
        const id = sessionId.value
        if (!id) return
        const cached = getCachedSession(id)
        if (cached) cached.detail.feedback = { ...feedback }
        try {
            await agentApi().messageFeedback(id, { message_id: messageId, rating, run_id: state.messages[messageId]?.runId ?? null })
        } catch {
            if (prev) feedback[messageId] = prev
            else delete feedback[messageId]
        }
    }

    return {
        state,
        status,
        feedback,
        access,
        share,
        rate,
        loading,
        sending,
        awaiting,
        error,
        errorInfo,
        working: computed(() => isWorking(state)),
        pending: computed(() => pendingApprovals(state)),
        send,
        cancel,
        actOnChangeset,
        undoRecords,
        resolveComposerEdit,
        reopenComposerEdit,
        reload: () => load(sessionId.value),
        ensureSession,
    }
}

export type AgentSession = ReturnType<typeof useAgentSession>

/** What a message with attachments and no words says (it is also what the transcript shows). */
export const FILE_ONLY_MESSAGE = 'Take a look at the attached.'

/** The agent's latest reply in the conversation (for a notification body). */
function lastAssistantText(state: SessionState): string | null {
    for (let i = state.timeline.length - 1; i >= 0; i--) {
        const t = state.timeline[i]
        if (t.kind !== 'message') continue
        const m = state.messages[t.id]
        if (m?.role === 'assistant' && m.text) return m.text
    }
    return null
}

/** "draft a check-in to…" → "Draft a check-in to…" (first line, ~60 chars). Mirrors the backend's quick title. */
export function quickTitle(text: string): string {
    const first = (text.split('\n').find((l) => l.trim()) ?? '').trim().replace(/\s+/g, ' ')
    const cut = first.length > 60 ? `${first.slice(0, 60).replace(/\s+\S*$/, '').replace(/[,.;:]$/, '')}…` : first
    return cut ? cut[0].toUpperCase() + cut.slice(1) : 'New conversation'
}

function onBeforeUnmountSafe(fn: () => void): void {
    try {
        onBeforeUnmount(fn)
    } catch {
        /* used outside a component (tests) */
    }
}
